// ── PERIOD RANGES ─────────────────────────────────────────────────────────
// THE single source of truth for what "Today / Week / Month / Year / Custom"
// mean, so every surface (dashboard KPIs, Sales/Purchases/Expenses tabs, wallet
// reports, drilldowns, the Business Statement, and exports) filters by the SAME
// dates. Previously the dashboard capped at today while the wallet reports used
// the full calendar month/year, so the same label could cover different ranges.
//
// CONVENTION (chosen app-wide):
//   • Today  = today only
//   • Week   = the last 7 days including today (rolling)
//   • Month  = 1st of the current month → today        (month-to-date)
//   • Year   = Jan 1 of the current year → today       (year-to-date)
//   • Custom = the user's chosen from/to (may include future dates)
// Preset ranges therefore NEVER include future-dated transactions; only an
// explicit Custom range can. Dates are 'YYYY-MM-DD' strings compared
// lexicographically — timezone-independent, matching the cash ledger.
//
// Pure: no React, no storage. Locked down by periods.test.js.

export const ymd = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

// Normalize any stored date (ISO timestamp or 'YYYY-MM-DD') to a comparable key.
export const dateKey = (v) => (v ? String(v).slice(0, 10) : '');

// { from, to } inclusive 'YYYY-MM-DD' strings for a period. `opts.now` (a Date
// or date string) lets tests pin "today"; defaults to the real now.
export const getPeriodRange = (period, customStart, customEnd, opts = {}) => {
  const now = opts.now ? new Date(opts.now) : new Date();
  const today = ymd(now);
  switch (period) {
    case 'custom':
      return { from: customStart ? dateKey(customStart) : null, to: customEnd ? dateKey(customEnd) : null };
    case 'today':
      return { from: today, to: today };
    case 'week': {
      const ws = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      ws.setDate(ws.getDate() - 6); // last 7 days incl. today
      return { from: ymd(ws), to: today };
    }
    case 'month':
      return { from: ymd(new Date(now.getFullYear(), now.getMonth(), 1)), to: today };
    case 'year':
      return { from: ymd(new Date(now.getFullYear(), 0, 1)), to: today };
    default:
      // Safe default = month-to-date.
      return { from: ymd(new Date(now.getFullYear(), now.getMonth(), 1)), to: today };
  }
};

// Is a record's date within a { from, to } range (inclusive)? A missing `from`
// or `to` is treated as unbounded on that side.
export const inPeriod = (recordDate, range) => {
  const d = dateKey(recordDate);
  if (!d) return false;
  if (range && range.from && d < range.from) return false;
  if (range && range.to && d > range.to) return false;
  return true;
};

// Filter an array of records ({ date }) to a period.
export const filterRecordsByPeriod = (arr, period, customStart, customEnd, opts) => {
  const range = getPeriodRange(period, customStart, customEnd, opts);
  return (arr || []).filter((r) => inPeriod(r && r.date, range));
};
